import React, { useEffect, useMemo, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import DateInput from '../common/DateInput';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { ProgressBar } from 'primereact/progressbar';
import { driversApi, exportApi } from '../../api/services';
import { useAlerts } from '../../custom/Alerts';
import { addDays, addMonths, fmtDate, startOfToday } from '../../utils/dates';
import { driverLabel } from '../../utils/activities';
import { blobError, download, useReportCompanies, useReportStatus } from './reportUtils';
import IANA_TIME_ZONES from '../../utils/timezones';

const pad = (n) => String(n).padStart(2, '0');

/** Stari spisak IANA zona sa trenutnim UTC offsetom: "(UTC+01:00) Europe/Belgrade". */
const buildZones = () => {
    const now = new Date();
    return IANA_TIME_ZONES.map((tz) => {
        let off = 0;
        try {
            const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'longOffset' }).formatToParts(now);
            const v = parts.find((p) => p.type === 'timeZoneName').value; // "GMT+01:00"
            const m = /GMT([+-])(\d{2}):(\d{2})/.exec(v);
            off = m ? (m[1] === '-' ? -1 : 1) * (+m[2] * 60 + +m[3]) : 0;
        } catch { /* UTC */ }
        const sign = off < 0 ? '-' : '+';
        const a = Math.abs(off);
        return { value: tz, label: `(UTC${sign}${pad(Math.floor(a / 60))}:${pad(a % 60)}) ${tz}`, off };
    });
};

/* Nedeljni (exportType 1) i kvartalni (3) izveštaj za kompaniju ili vozača. */
export default function ReportForm({ exportType, titlePrefix, onClose }) {
    const alerts = useAlerts();
    const { companies, company, setCompany } = useReportCompanies(false);
    const status = useReportStatus();
    const zones = useMemo(buildZones, []);
    const [mode, setMode] = useState('report-company');
    const [drivers, setDrivers] = useState([]);
    const [driver, setDriver] = useState(null);
    const today = startOfToday();
    const [from, setFrom] = useState(exportType === 1 ? addDays(today, -7) : addMonths(today, -3));
    const [to, setTo] = useState(today);
    const [publicTransport, setPublicTransport] = useState(true);
    const [zone, setZone] = useState('Europe/Belgrade');

    useEffect(() => {
        driversApi.all({ companyIds: [] }).then((r) => { setDrivers(r.drivers); setDriver(r.drivers[0] || null); }).catch(() => {});
    }, []);

    const valid = from && to && (mode === 'report-company' ? !!company : !!driver);

    const generate = async () => {
        status.start();
        try {
            const res = await exportApi.report({
                exportDataType: mode, companyId: company ? company.id : null, driverId: driver ? driver.id : null,
                startDate: from, endDate: to, exportType, isPublicTransport: publicTransport, ianaTimeZone: zone,
            });
            const range = `${fmtDate(from)} - ${fmtDate(to)}`;
            download(res, mode === 'report-company' ? `${company.name} - ${range}.zip` : `${driver.lastName} ${driver.firstName} - ${range}.xlsx`);
            status.done();
        } catch (e) {
            status.fail();
            alerts.error(await blobError(e));
        }
    };

    const subject = mode === 'report-company' ? company && company.name : driver && `${driver.lastName} ${driver.firstName}`;

    return (
        <Dialog visible header={`${titlePrefix} izveštaj`} onHide={onClose} style={{ width: '52rem', maxWidth: '96vw' }} modal
            footer={<ReportFooter onClose={onClose} onGenerate={generate} disabled={!valid || status.state.busy} busy={status.state.busy} />}>
            <div className="form-grid cols-2">
                <div className="field"><label htmlFor="rf-mode">Generišite izveštaj za</label>
                    <Dropdown inputId="rf-mode" value={mode} onChange={(e) => setMode(e.value)}
                        options={[{ label: 'Kompaniju', value: 'report-company' }, { label: 'Vozača', value: 'report-driver' }]} /></div>
                {mode === 'report-company' ? (
                    <div className="field"><label htmlFor="rf-subject">Kompanija</label>
                        <Dropdown inputId="rf-subject" value={company} options={companies} optionLabel="name" dataKey="id" filter onChange={(e) => setCompany(e.value)} /></div>
                ) : (
                    <div className="field"><label htmlFor="rf-subject">Vozač</label>
                        <Dropdown inputId="rf-subject" value={driver} options={drivers.map((d) => ({ label: driverLabel(d), value: d }))} dataKey="id" filter
                            virtualScrollerOptions={drivers.length > 10 ? { itemSize: 40 } : undefined} onChange={(e) => setDriver(e.value)} /></div>
                )}
                <div className="field"><label htmlFor="rf-from">Od</label>
                    <DateInput inputId="rf-from" value={from} onChange={(e) => setFrom(e.value)} dateFormat="dd.mm.yy" showIcon className={!from ? 'p-invalid' : ''} /></div>
                <div className="field"><label htmlFor="rf-to">Do</label>
                    <DateInput inputId="rf-to" value={to} onChange={(e) => setTo(e.value)} dateFormat="dd.mm.yy" showIcon className={!to ? 'p-invalid' : ''} /></div>
                <div className="field"><label htmlFor="rf-zone">Vremenska zona</label>
                    <Dropdown inputId="rf-zone" value={zone} options={zones} filter virtualScrollerOptions={{ itemSize: 40 }} onChange={(e) => setZone(e.value)} /></div>
                <div className="field" style={{ justifyContent: 'flex-end' }}>
                    <label className="check" htmlFor="rf-public"><Checkbox inputId="rf-public" checked={publicTransport} onChange={(e) => setPublicTransport(e.checked)} /> Gradski prevoz</label></div>
            </div>
            {valid && (
                <p className="report-summary"><i className="pi pi-info-circle" aria-hidden="true" style={{ marginRight: '.5rem' }} />Biće generisan izveštaj za {mode === 'report-company' ? 'kompaniju' : 'vozača'} <b>{subject}</b> za
                    period od <b>{fmtDate(from)}</b> do <b>{fmtDate(to)}</b></p>
            )}
            <ReportProgress status={status.state} />
        </Dialog>
    );
}

export function ReportProgress({ status }) {
    if (!status.text) return null;
    return (
        <div className="report-progress" aria-live="polite">
            <div className="report-progress-head">
                <span>{status.text}</span>{status.percent > 0 && <span>{status.percent}%</span>}
            </div>
            <ProgressBar mode={status.busy ? 'indeterminate' : 'determinate'} value={status.percent} showValue={false} />
        </div>
    );
}

/** Podnožje prozora izveštaja: Zatvori + Generiši izveštaj. */
export function ReportFooter({ onClose, onGenerate, disabled, busy }) {
    return (
        <div className="dialog-footer">
            <Button label="Zatvori" severity="secondary" text onClick={onClose} />
            <Button label="Generiši izveštaj" icon="pi pi-download" onClick={onGenerate} disabled={disabled} loading={busy} />
        </div>
    );
}
